import { lazy, Suspense, type JSX } from 'react';

/**
 * R15 (round-6) — Monaco diff viewer loaded via React.lazy.
 *
 * Monaco ships ~4 MB of chunks; importing it at route-load time
 * blows past the dashboard's 300 KB gzip budget. This wrapper
 * dynamically imports `@monaco-editor/react` only when the component
 * actually renders — approvals / lifecycle / artifacts pages pull it
 * on demand, everything else never touches the chunk.
 *
 * When `@monaco-editor/react` is absent from `node_modules`, the
 * lazy import rejects and the Suspense boundary renders the
 * provided fallback; callers can fall back to the existing
 * `react-diff-viewer-continued` path.
 */
const MonacoDiffImpl = lazy(async () => {
  const mod = await import('@monaco-editor/react').catch(() => null);
  if (!mod) {
    return {
      default: function MissingMonacoEditor(): JSX.Element {
        return (
          <div className="shakti-card text-sm text-fg-secondary">
            Monaco editor is not installed. Install{' '}
            <code>@monaco-editor/react</code> to enable the rich diff view;
            falling back to the plain diff.
          </div>
        );
      },
    };
  }
  const DiffEditor = mod.DiffEditor;
  return {
    default: function WrappedDiff({
      original,
      modified,
      language,
      height,
    }: {
      original: string;
      modified: string;
      language?: string;
      height?: number;
    }): JSX.Element {
      return (
        <DiffEditor
          original={original}
          modified={modified}
          language={language ?? 'plaintext'}
          height={height ?? 480}
          theme="vs-dark"
          options={{
            renderSideBySide: true,
            readOnly: true,
            minimap: { enabled: false },
          }}
        />
      );
    },
  };
});

export interface MonacoDiffEditorProps {
  original: string;
  modified: string;
  language?: string;
  height?: number;
}

export function MonacoDiffEditor(props: MonacoDiffEditorProps): JSX.Element {
  return (
    <Suspense
      fallback={
        <div
          role="status"
          aria-live="polite"
          className="shakti-card text-sm text-fg-secondary"
        >
          Loading editor…
        </div>
      }
    >
      <MonacoDiffImpl {...props} />
    </Suspense>
  );
}
